// src/encounter/lib/to-vaccination-entry.ts
// Map a VaccinationDraft (from VaccinationAdminEditor) → the AssessmentPlanVaccination wire entry
// (lot-aware admin, PROD-1227). Curl-verified shape 2026-06-18: 6-field Codings with PascalCase
// Definition; admin/dose Unit are Codings built from the UoM string; dates no-Z ISO; orderId null
// on create (server assigns — it's the id that flows to the Inventory deduction).
import type { VaccinationDraft, Coding } from '../vaccination-admin-editor';
import type { AssessmentCoding, AssessmentPlanVaccination } from '@/types/assessment-api';

/** 3-field NAB coding → 6-field wire coding (Definition/description/shortName null). */
function code6(c: Coding | null): AssessmentCoding | null {
  return c
    ? { code: c.code, Definition: null, display: c.display, system: c.system, description: null, shortName: null }
    : null;
}
/** UoM string ("ML") → Coding {code, display, system:null}. */
function unitCoding(u: string | null): AssessmentCoding | null {
  return u ? { code: u, Definition: null, display: u, system: null, description: null, shortName: null } : null;
}

export interface VaccinationEntryContext {
  encounter: string;
  subject: string; // patientId
  performer: string; // provider userId
  /** Preserved on edit; null on a fresh add (server assigns). */
  existing?: Pick<AssessmentPlanVaccination, 'identifier' | 'parentId' | 'assessmentPlanId' | 'orderId'> | null;
}

export function vaccinationDraftToEntry(
  draft: VaccinationDraft,
  ctx: VaccinationEntryContext,
): AssessmentPlanVaccination {
  const e = ctx.existing;
  return {
    identifier: e?.identifier ?? null,
    parentId: e?.parentId ?? null,
    encounter: ctx.encounter,
    assessmentPlanId: e?.assessmentPlanId ?? null,
    subject: ctx.subject,
    performer: ctx.performer,
    vaccine: code6({
      code: draft.vaccine.valueCoding.code,
      display: draft.vaccine.valueCoding.display,
      system: draft.vaccine.valueCoding.system,
    }),
    status: null,
    dataAbsentReason: null,
    administratedDate: draft.administratedDate,
    quantityValue: null,
    quantityCode: null,
    quantitySystem: null,
    statusReason: null,
    isActive: e ? true : null, // null on create, true on update (curl-observed)
    isDeleted: e ? false : null,
    createdBy: null,
    createdDate: null,
    updatedBy: null,
    updatedDate: null,
    lotNo: draft.lotNo || null,
    expiryDate: draft.expiryDate,
    site: code6(draft.site),
    remarks: draft.remarks || null,
    administrationAmount: draft.administrationAmount,
    administrationUnit: unitCoding(draft.administrationUnit),
    administrationDoseQuantity: draft.administrationDoseQuantity,
    administrationDoseUnit: unitCoding(draft.administrationDoseUnit),
    manufacturerName: null,
    route: code6(draft.route),
    repeatPattern: code6(draft.repeatPattern),
    durationQuantity: code6(draft.durationQuantity),
    durationUnits: code6(draft.durationUnits),
    orderId: e?.orderId ?? null,
    performerDetails: null,
    isAdministrationRequested: draft.isAdministrationRequested,
  };
}
